import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, CalendarDays, Users, AlertTriangle, XCircle } from "lucide-react";
import { api, fmtDateTime, euro, apiError } from "@/lib/api";
import { toast } from "sonner";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-4 py-10">
    <div className="w-full max-w-lg">
      <div className="mb-8">
        <p className="font-heading text-xl uppercase tracking-tight text-white leading-none">Golfcoaching Academy</p>
        <p className="font-mono-lab text-[10px] tracking-[0.3em] text-[#ff0033] uppercase mt-1">Wendlingen · by golfcoaching.online</p>
      </div>
      {children}
      <p className="text-center text-neutral-600 text-xs mt-10">Michael Wolf Golfprofessional · training@wolfmichael.de</p>
    </div>
  </div>
);

const StornoPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const loadData = () => api.get(`/storno/${token}`).then((r) => setData(r.data)).catch(() => setError("Dieser Stornolink ist ungültig."));
  useEffect(() => { loadData(); }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/storno/${token}/confirm`);
      setDone(res);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  if (error) return <Shell><div className="text-center py-16"><XCircle className="mx-auto text-[#ff0033] mb-3" size={48} /><p className="text-[#ff0033] font-heading text-2xl uppercase">Link ungültig</p><p className="text-neutral-400 mt-3">{error}</p></div></Shell>;
  if (!data) return <Shell><div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#ff0033]" size={32} /></div></Shell>;

  if (done || data.already_cancelled) return (
    <Shell><div className="text-center py-14" data-testid="storno-done">
      <CheckCircle2 className="mx-auto text-green-500 mb-4" size={56} />
      <p className="font-heading text-3xl uppercase text-white">Buchung storniert</p>
      <p className="text-neutral-300 mt-3">{done?.refunded ? "Der bezahlte Betrag wird automatisch über die ursprüngliche Zahlungsart erstattet. Je nach Anbieter kann dies einige Werktage dauern." : "Ihre Stornierung wurde erfasst. Eine Bestätigung erhalten Sie per E-Mail."}</p>
    </div></Shell>
  );

  return (
    <Shell>
      <p className="font-mono-lab text-[11px] tracking-[0.25em] text-[#ff0033] uppercase mb-2">// Buchung stornieren</p>
      <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-white mb-6">Stornierung</h1>

      <div className="bg-[#0F0F0F] border border-white/10 p-5 mb-5" data-testid="storno-summary">
        <p className="font-heading text-xl text-white mb-3">{data.course_name}</p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-neutral-300"><CalendarDays size={15} className="text-[#ff0033]" /> {fmtDateTime(data.start, "de")}</div>
          <div className="flex items-center gap-2 text-neutral-300"><Users size={15} className="text-[#ff0033]" /> {data.customer_name} · {data.persons} {data.persons === 1 ? "Person" : "Personen"}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 border-t border-white/10 mt-4 pt-4 text-sm">
          <div><p className="text-neutral-500 text-xs uppercase font-mono-lab tracking-widest">Gezahlt</p><p className="text-white font-heading text-lg">{euro(data.amount)}</p></div>
          <div><p className="text-neutral-500 text-xs uppercase font-mono-lab tracking-widest">Bis Kursbeginn</p><p className="text-white font-heading text-lg">{data.hours_until_start > 0 ? `${Math.floor(data.hours_until_start)} Std.` : "abgelaufen"}</p></div>
        </div>
      </div>

      {data.within_deadline ? (
        <>
          <div className="border border-green-500/30 bg-green-500/10 p-4 mb-5 text-sm text-neutral-200" data-testid="storno-eligible">
            Die kostenfreie Stornierungsfrist (48 Stunden) ist noch nicht abgelaufen.{data.refund_eligible ? " Der bezahlte Betrag wird automatisch über die ursprüngliche Zahlungsart vollständig erstattet." : ""}
          </div>
          <button onClick={confirm} disabled={busy} data-testid="storno-confirm-btn" className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-4 hover:bg-[#cc0029] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={18} /> : null} Stornierung jetzt bestätigen
          </button>
        </>
      ) : (
        <div className="border border-amber-500/40 bg-amber-500/10 p-5 text-sm text-amber-200" data-testid="storno-deadline-passed">
          <AlertTriangle size={20} className="mb-2" />
          Die kostenfreie Stornierungsfrist von 48 Stunden ist bereits abgelaufen. Eine automatische Rückerstattung ist daher nicht mehr möglich. Bitte kontaktieren Sie die Golfcoaching Academy unter <a href="mailto:training@wolfmichael.de" className="text-white underline">training@wolfmichael.de</a>, falls ein besonderer Ausnahmefall vorliegt.
        </div>
      )}
    </Shell>
  );
};

export default StornoPage;
